import axios, {
  AxiosInstance,
  AxiosError,
  InternalAxiosRequestConfig,
} from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.daily2k.ng/api';

// ── Token helpers ─────────────────────────────────────────────────────────────
// Admin uses localStorage — acceptable for an internal tool on a managed device.
// Tokens are never logged or sent to third-party endpoints.
const getToken = () => localStorage.getItem('adminToken');
const getRefresh = () => localStorage.getItem('adminRefreshToken');
const saveToken = (access: string, refresh?: string) => {
  localStorage.setItem('adminToken', access);
  if (refresh) localStorage.setItem('adminRefreshToken', refresh);
};
const clearTokens = () => {
  localStorage.removeItem('adminToken');
  localStorage.removeItem('adminRefreshToken');
};

// ── Queue for concurrent 401s during a refresh ────────────────────────────────
let isRefreshing = false;
let refreshQueue: Array<{
  resolve: (token: string) => void;
  reject: (err: unknown) => void;
}> = [];

const processQueue = (error: unknown, token: string | null) => {
  refreshQueue.forEach(({ resolve, reject }) =>
    error ? reject(error) : resolve(token!)
  );
  refreshQueue = [];
};

// ── Axios instance ────────────────────────────────────────────────────────────
const apiClient: AxiosInstance = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30_000,
});

// ── Request interceptor ───────────────────────────────────────────────────────
apiClient.interceptors.request.use((config) => {
  const token = getToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ── Response interceptor — refresh before logout ──────────────────────────────
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    // Guard: only handle 401s; never retry the refresh endpoint itself.
    if (
      error.response?.status !== 401 ||
      original._retry ||
      original.url?.includes('/token/refresh/')
    ) {
      return Promise.reject(error);
    }

    const refresh = getRefresh();
    if (!refresh) {
      clearTokens();
      window.location.href = '/login';
      return Promise.reject(error);
    }

    // Queue concurrent 401s while one refresh is in flight.
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        refreshQueue.push({
          resolve: (token) => {
            original.headers.Authorization = `Bearer ${token}`;
            resolve(apiClient(original));
          },
          reject,
        });
      });
    }

    original._retry = true;
    isRefreshing = true;

    try {
      const { data } = await axios.post(
        `${API_URL}/token/refresh/`,
        { refresh },
        { timeout: 15_000 }
      );

      const newAccess: string = data.access;
      const newRefresh: string | undefined = data.refresh;
      saveToken(newAccess, newRefresh);
      processQueue(null, newAccess);

      original.headers.Authorization = `Bearer ${newAccess}`;
      return apiClient(original);
    } catch (refreshError) {
      processQueue(refreshError, null);
      clearTokens();
      window.location.href = '/login';
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

export default apiClient;
